import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { fromEvent, merge, interval, Subject, Subscription } from 'rxjs';
import { debounceTime, take, takeUntil } from 'rxjs/operators';

import { AuthRepository } from './auth.repository';
import { PresenceService } from '@app/services/presence.service';
import { CompletionFlowService } from '@app/services/completion-flow.service';
import { MediaActivityService } from '@app/services/media-activity.service';
import { VirtualClassroomService } from '@app/services/vclassroom.service';

const CONFIG_KEY = 'INACTIVITY_TIMEOUT_MINUTES';
const DEFAULT_TIMEOUT_MINUTES = 15;

@Injectable({ providedIn: 'root' })
export class IdleLogoutService implements OnDestroy {
  private _timeoutHandle: ReturnType<typeof setTimeout> | null = null;
  private _activitySub: Subscription | null = null;
  private _visibilitySub: Subscription | null = null;
  private _videoCheckSub: Subscription | null = null;
  private _destroy$ = new Subject<void>();
  private _isRunning = false;
  private _timeoutMs = DEFAULT_TIMEOUT_MINUTES * 60 * 1000;

  /** Activity events that reset the idle timer */
  private readonly _activityEvents = [
    'mousemove',
    'mousedown',
    'keydown',
    'scroll',
    'touchstart',
    'click',
  ];

  constructor(
    private _router: Router,
    private _ngZone: NgZone,
    private _authRepository: AuthRepository,
    private _presenceService: PresenceService,
    private _completionFlowService: CompletionFlowService,
    private _mediaActivity: MediaActivityService,
    private _vcService: VirtualClassroomService,
  ) {}

  /**
   * Start the idle logout timer.
   * Fetches the timeout from company config; does nothing if timeout is 0 (disabled).
   */
  start(): void {
    if (this._isRunning) {
      return;
    }

    // Load configured timeout from server, then start the timer
    this._completionFlowService.getCompanyConfig(CONFIG_KEY)
      .pipe(take(1))
      .subscribe({
        next: (config) => {
          const minutes = config?.value !== undefined
            ? parseInt(config.value, 10)
            : DEFAULT_TIMEOUT_MINUTES;
          this._startWithTimeout(isNaN(minutes) ? DEFAULT_TIMEOUT_MINUTES : minutes);
        },
        error: () => {
          // Server unreachable or key not set: use default
          this._startWithTimeout(DEFAULT_TIMEOUT_MINUTES);
        },
      });
  }

  private _startWithTimeout(minutes: number): void {
    if (minutes <= 0) {
      return;
    }

    this._timeoutMs = minutes * 60 * 1000;
    this._isRunning = true;

    this._ngZone.runOutsideAngular(() => {
      const activityStream$ = merge(
        ...this._activityEvents.map((event) => fromEvent(document, event))
      ).pipe(
        debounceTime(1000),
        takeUntil(this._destroy$)
      );

      this._activitySub = activityStream$.subscribe(() => {
        this._resetTimer();
      });

      // Pause/resume timer when tab visibility changes
      const visibility$ = fromEvent(document, 'visibilitychange').pipe(
        takeUntil(this._destroy$)
      );

      this._visibilitySub = visibility$.subscribe(() => {
        if (document.visibilityState === 'visible') {
          this._resetTimer();
        } else {
          this._clearTimer();
        }
      });

      // Check every 30 s whether a recorded lesson video is actively playing.
      // If so, treat it as user activity so a long video never triggers logout.
      // The virtual classroom does NOT set this flag — idle logout still applies there.
      this._videoCheckSub = interval(30_000)
        .pipe(takeUntil(this._destroy$))
        .subscribe(() => {
          if (this._mediaActivity.isWatchingLesson()) {
            this._resetTimer();
          }
        });
    });

    this._resetTimer();
  }

  /**
   * Stop the idle logout timer and clean up all listeners.
   */
  stop(): void {
    this._isRunning = false;
    this._clearTimer();
    this._activitySub?.unsubscribe();
    this._activitySub = null;
    this._visibilitySub?.unsubscribe();
    this._visibilitySub = null;
    this._videoCheckSub?.unsubscribe();
    this._videoCheckSub = null;
  }


  /**
   * Restart the service with a potentially updated timeout value.
   * Call this after company settings are refreshed.
   */
  restart(): void {
    this.stop();
    this.start();
  }

  private _resetTimer(): void {
    this._clearTimer();
    this._timeoutHandle = setTimeout(() => {
      this._ngZone.run(() => this._onTimeout());
    }, this._timeoutMs);
  }

  private _clearTimer(): void {
    if (this._timeoutHandle !== null) {
      clearTimeout(this._timeoutHandle);
      this._timeoutHandle = null;
    }
  }

  private _onTimeout(): void {
    if (!this._authRepository.getAccessToken()) {
      this.stop();
      return;
    }

    // Protected mode: if the student is in an active consulting session, do NOT log out.
    // Reset the timer instead and try again after the configured timeout.
    if (this._vcService.isInProtectedSession$.value) {
      this._resetTimer();
      return;
    }

    this.stop();
    this._presenceService.stopPresence(true);
    this._authRepository.logout();
    this._router.navigate(['/sign-in'], {
      queryParams: { reason: 'inactivity' },
    });
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
    this.stop();
  }
}
